import { SortDirection } from "@episerver/ui-framework";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { Property } from "../../../businessFoundation/MetaClassData/MetaClassDataList/MetaClassDataModel";
import { RootState } from "../../store";
import { ContactNote, DeleteDialogParams, PaginationModel, SortModel } from "../model/ContactNote";
import { deleteContactNotes, fetchContactNoteById, fetchContactNotes } from "./asyncThunks";

export interface ContactNoteState {
    contactNotes: ContactNote[];
    contactNote: ContactNote;
    selectedContactNotes: string[];
    pagination: PaginationModel;
    sort: SortModel;
    dialogStatus: boolean;
    confirmDialogStatus: DeleteDialogParams;
}

export const initialContactNoteState: ContactNoteState = {
    contactNotes: [],
    contactNote: {
        contactNoteId: null,
        created: null,
        modified: null,
        noteTitle: "",
        noteContent: "",
        contactId: null,
        contactName: null,
        extendedProperties: [] as Property[],
        isFetched: false,
        isNew: true,
        idx: 1
    },
    selectedContactNotes: [] as string[],
    pagination: {
        startRow: 0,
        rowsPerPage: 10,
        rowsPerPageOptions: [10, 20, 50, 100],
        totalRowCount: 0,
    },
    sort: {
        sortedColumn: "",
        sortDirection: SortDirection.Unsorted,
    } as SortModel,
    dialogStatus: false,
    confirmDialogStatus: {
        dialogOpen: false,
        contactNoteIds: [],
    },
};

export const contactNoteManagerSlice = createSlice({
    name: "contactNoteManager",
    initialState: initialContactNoteState,
    reducers: {
        selectContactNote: (state, action) => {
            state.selectedContactNotes.push(action.payload);
        },
        deselectContactNote: (state, action) => {
            state.selectedContactNotes = state.selectedContactNotes.filter(
                (contactNoteId: string) => contactNoteId !== action.payload
            );
        },
        selectAllContactNotes: (state, action) => {
            state.selectedContactNotes = action.payload.map((contactNote: ContactNote) => contactNote.contactNoteId!);
        },
        deselectAllContactNotes: (state) => {
            state.selectedContactNotes = [];
        },
        sort: (state, action): void => {
            const { column, direction } = action.payload;
            state.sort = { sortDirection: direction, sortedColumn: column };
            state.contactNotes = state.contactNotes.sort((a: ContactNote, b: ContactNote) => {
                const aValue = (a[column as keyof typeof a] || "").toString();
                const bValue = (b[column as keyof typeof a] || "").toString();

                if (direction === SortDirection.Ascending) {
                    return aValue.localeCompare(bValue, undefined, { numeric: true });
                }
                return bValue.localeCompare(aValue, undefined, { numeric: true });
            });
        },
        onChangeContactNotePage: (state, action): void => {
            state.pagination.startRow = action.payload;
        },
        onChangeContactNoteRowPerPage: (state, action): void => {
            state.pagination.rowsPerPage = action.payload;
        },
        onChangeDialogStatus: (state, action): void => {
            state.dialogStatus = action.payload.dialogStatus;
            state.contactNote = action.payload.contactNote;
        },
        onChangeConfirmDialogStatus: (state, action): void => {
            state.confirmDialogStatus.dialogOpen = action.payload.dialogOpen;
            state.confirmDialogStatus.contactNoteIds = action.payload.contactNoteIds;
        },
        onAddOrUpdate: (state, action): void => {
            if (action.payload.contactNoteId != null && action.payload.contactNoteId.trim().length > 0) {
                state.contactNotes = state.contactNotes.map((t) => {
                    if (t.contactNoteId === action.payload.contactNoteId) return action.payload;
                    return t;
                });
            } else {
                state.contactNotes.push(action.payload);
            }
        },
        onDelete: (state, action): void => {
            let contactNotes = [...state.contactNotes];
            contactNotes.splice(action.payload, 1);
            state.contactNotes = contactNotes;
        },
    },
    extraReducers: {
        [`${fetchContactNotes.fulfilled}`]: (state, action): void => {
            state.contactNotes = action.payload;
            state.pagination.startRow = 0;
            state.pagination.totalRowCount = state.contactNotes.length;
        },
        [`${deleteContactNotes.fulfilled}`]: (state, action): void => {
            state.contactNotes = action.payload;
            state.selectedContactNotes = [];
            state.pagination.startRow = 0;
            state.pagination.totalRowCount = state.contactNotes.length;
        },
        [`${fetchContactNoteById.fulfilled}`]: (state, action): void => {
            state.contactNote = { ...action.payload, isFetched: true, isNew: state.contactNote.isNew, idx: state.contactNote.idx};
        },
    },
});

// Actions
export const {
    selectContactNote,
    deselectContactNote,
    selectAllContactNotes,
    deselectAllContactNotes,
    sort,
    onChangeContactNotePage,
    onChangeContactNoteRowPerPage,
    onChangeDialogStatus,
    onChangeConfirmDialogStatus,
    onAddOrUpdate,
    onDelete,
} = contactNoteManagerSlice.actions;

// Selectors
export const contactNotesSelector = createSelector(
    (state: RootState): ContactNote[] => state.contactNoteManager.contactNotes,
    (contactNotes: ContactNote[]): ContactNote[] => contactNotes
);

export const selectedContactNotesSelector = createSelector(
    (state: RootState): string[] => state.contactNoteManager.selectedContactNotes,
    (selectedContactNotes: string[]): string[] => selectedContactNotes
);

export const contactNoteSelector = createSelector(
    (state: RootState): ContactNote => state.contactNoteManager.contactNote,
    (contactNote: ContactNote): ContactNote => contactNote
);

export const dialogStatusSelector = createSelector(
    (state: RootState): boolean => state.contactNoteManager.dialogStatus,
    (dialogStatus: boolean): boolean => dialogStatus
);

export const confirmDialogStatusSelector = createSelector(
    (state: RootState): DeleteDialogParams => state.contactNoteManager.confirmDialogStatus,
    (confirmDialogStatus: DeleteDialogParams): DeleteDialogParams => confirmDialogStatus
);

export const paginationSelector = createSelector(
    (state: RootState): PaginationModel => state.contactNoteManager.pagination,
    (pagination: PaginationModel): PaginationModel => pagination
);
